import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { toast } from 'react-toastify';
import { theme } from '../styles/theme';
import Card from '../components/common/Card';
import Table from '../components/common/Table';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import Select from 'react-select';
import Papa from 'papaparse';
import { certificateUserAPI } from '../utils/api';
import { Upload, Download, Edit, Mail, Loader, X, AlertCircle, CheckCircle } from 'react-feather';

const Container = styled.div`
  display: grid;
  gap: ${theme.spacing.lg};
`;

const Controls = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing.md};
`;

const FileInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const FileInputLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md};
  border: 2px dashed ${theme.colors.primary};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
`;

const FileInput = styled.input`
  display: none;
`;

const SearchSection = styled.div`
  flex: 1;
  max-width: 400px;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  width: 100%;

  .clear-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    padding: 4px;
    color: ${theme.colors.text.secondary};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
  }
`;

const IconButton = styled(Button)`
  min-width: 28px;
  width: 28px;
  height: 28px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  svg {
    width: 14px;
    height: 14px;
    transition: all 0.3s ease;
  }

  .loader {
    position: absolute;
    animation: spin 1s linear infinite;
    color: currentColor;
  }
`;

const domainOptions = [
  { value: '30 Days Python Bootcamp', label: '30 Days Python Bootcamp' },
  { value: '21 Days AI-Powered UI/UX Design Bootcamp', label: '21 Days AI-Powered UI/UX Design Bootcamp' },
  { value: 'DPC-2025', label: 'Daily Programming Challenge -2025' },
  { value: '7-Day AI-Powered DSA Bootcamp', label: '7-Day AI-Powered DSA Bootcamp' },

];

const durationOptions = [
  { value: '30', label: '30 Days' },

];

const CertificateUsers = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [sendingMap, setSendingMap] = useState({});
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const pageSize = 10;

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter(r =>
      r.fullName?.toLowerCase().includes(term) ||
      r.email?.toLowerCase().includes(term) ||
      String(r.id).includes(term)
    );
  }, [rows, searchTerm]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const data = await certificateUserAPI.fetchAll(
        searchTerm,
        selectedDomain?.value || '',
        selectedDuration?.value || '',
        selectedStatus?.value || 'ALL'
      );
      setRows(data || []);
    } catch (err) {
      toast.error('Failed to fetch certificate users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedDomain, selectedDuration, selectedStatus]);

  const handleDownloadTemplate = async () => {
    try {
      const blob = await certificateUserAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'certificate_users_template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (result) => {
        const cleanedData = result.data.map((item) => ({
          'Full Name': item['Full Name']?.trim() || '',
          'Email': item['Email']?.trim() || '',
          'Duration': item['Duration']?.trim() || '30',
          'Date': item['Date']?.trim() || '',
          'SendingDate': item['SendingDate']?.trim() || '',
          'domain': item['domain']?.trim() || '',
          'cerNo': item['cerNo']?.trim() || '',
        }));

        try {
          await certificateUserAPI.uploadCsvJson(cleanedData);
          toast.success('CSV uploaded');
          fetchRows();
        } catch (e) {
          toast.error('Failed to upload CSV');
        }
      },
      error: () => toast.error('Error parsing CSV file'),
    });
  };

  const handleSendCertificate = async (userId) => {
    setSendingMap(prev => ({ ...prev, [userId]: true }));
    try {
      await certificateUserAPI.sendCertificateEmail(userId);
      toast.success('Certificate sent');
      fetchRows();
    } catch (e) {
      toast.error('Failed to send certificate');
    } finally {
      setSendingMap(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    setModalLoading(true);
    try {
      await certificateUserAPI.update(selectedUser.id, {
        fullName: selectedUser.fullName,
        email: selectedUser.email,
        duration: selectedUser.duration,
        date: selectedUser.date,
        sendingDate: selectedUser.sendingDate,
        domain: selectedUser.domain,
        cerNo: selectedUser.cerNo,
      });
      toast.success('User updated');
      setShowModal(false);
      fetchRows();
    } catch (e) {
      toast.error('Failed to update user');
    } finally {
      setModalLoading(false);
    }
  };

  const columns = [
    {
      key: 'srNo',
      label: 'Sr. No.',
      width: '70px',
      render: (row) => {
        const index = filteredRows.findIndex(u => u.id === row.id);
        return (
          <div style={{ textAlign: 'center', fontWeight: 500 }}>
            {index !== -1 ? index + 1 : '-'}
          </div>
        );
      },
    },
    { key: 'id', label: 'U-ID', width: '100px' },
    { key: 'fullName', label: 'Full Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true, render: (row) => (
      <a href={`mailto:${row.email}`} style={{ color: theme.colors.primary }}>{row.email}</a>
    ) },
    { key: 'domain', label: 'Domain', sortable: true },
    { key: 'duration', label: 'Duration', sortable: true, render: (row) => `${row.duration} days` },
    { key: 'date', label: 'Start Date', sortable: true, render: (row) => (
      <span style={{ whiteSpace: 'nowrap' }}>
        {row.date ? new Date(row.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}
      </span>
    ) },
    { key: 'sendingDate', label: 'Sending Date', sortable: true, render: (row) => (
      <span style={{ whiteSpace: 'nowrap' }}>
        {row.sendingDate ? new Date(row.sendingDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}
      </span>
    ) },
    { key: 'cerNo', label: 'Cert. No.', sortable: true },
    {
      key: 'actions',
      label: 'Actions',
      width: '120px',
      render: (row) => (
        <div className="d-flex gap-1 justify-content-center">
          <IconButton
            size="small"
            variant="outlined"
            onClick={() => { setSelectedUser(row); setShowModal(true); }}
            title="Edit User"
          >
            <Edit />
          </IconButton>
          <IconButton
            size="small"
            color="success"
            onClick={() => handleSendCertificate(row.id)}
            disabled={!!sendingMap[row.id]}
            title="Send Certificate"
            style={{opacity: row.completionsSent === "yes" ? 0.5 : 1}}
          >
            {sendingMap[row.id] ? <Loader className="loader" size={14} /> : <Mail />}
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <Container fluid className=''>
      <h1 className='fw-bold'>Certificate Users</h1>
      <div className=" w-100 d-flex gap-3">
        <div className='d-flex align-items-center justify-content-center g-2 w-50 p-2 bg-white rounded'
          style={{ boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)" }}>
          <Button color="primary" onClick={handleDownloadTemplate} startIcon={<Download />} className='w-100 h-fit p-3' fullWidth>
            Download CSV Template
          </Button>
        </div>

        <div  className='d-flex align-items-center justify-content-center gap-2 w-50 p-4 bg-white rounded'    style={{ boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)" }}>
          <FileInputWrapper>
            <FileInputLabel htmlFor="cert-users-upload">
              <Upload size={24} />
              <span>Click or drag CSV file to upload</span>
            </FileInputLabel>
            <FileInput id="cert-users-upload" type="file" accept=".csv" onChange={handleFileUpload} />
          </FileInputWrapper>
        </div>

        </div>

      <Controls>



        <SearchSection>
          <SearchInputWrapper>
            <Input
              placeholder="Search by name, email, or ID"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className='w-100'
            />
            {searchTerm && (
              <div className="clear-icon" onClick={() => setSearchTerm('')} title="Clear search">
                <X size={16} />
              </div>
            )}
          </SearchInputWrapper>
        </SearchSection>

        <SearchSection>
          <Select
            placeholder="Filter by Domain"
            value={selectedDomain}
            onChange={(option) => setSelectedDomain(option)}
            options={[{ value: '', label: 'All Domains' }, ...domainOptions]}
            isClearable
            className="basic-select"
            classNamePrefix="select"
          />
        </SearchSection>

        <SearchSection>
          <Select
            placeholder="Filter by Duration"
            value={selectedDuration}
            onChange={(option) => setSelectedDuration(option)}
            options={[{ value: '', label: 'All Durations' }, ...durationOptions]}
            isClearable
            className="basic-select"
            classNamePrefix="select"
          />
        </SearchSection>

        {/* <SearchSection>
          <Select
            placeholder="Filter by Status"
            value={selectedStatus}
            onChange={(option) => setSelectedStatus(option)}
            options={[
              { value: 'ALL', label: 'All Status' },
              { value: 'complection-sent', label: 'Completion Sent' },
              { value: 'complection-remaining', label: 'Completion Pending' },
              { value: 'offer-letter-sent', label: 'Offer Letter Sent' },
              { value: 'offer-letter-remaining', label: 'Offer Letter Pending' },
            ]}
            isClearable
            className="basic-select"
            classNamePrefix="select"
          />
        </SearchSection> */}
      </Controls>

      <Card style={{width:'100%', padding: '0px', overflowX: 'auto', margin: '0', borderRadius: 0, boxShadow: 'none', backgroundColor: 'transparent' }}>
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <Table
            columns={columns}
            data={filteredRows}
            loading={loading}
            pageSize={pageSize}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        </div>
      </Card>

      {showModal && (
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title="Update Certificate User"
          onSubmit={handleUpdateUser}
          loading={modalLoading}
        >
          <Input
            label="Full Name"
            value={selectedUser?.fullName || ''}
            onChange={(e) => setSelectedUser(prev => ({ ...prev, fullName: e.target.value }))}
            disabled={modalLoading}
          />
          <Input
            label="Email"
            type="email"
            value={selectedUser?.email || ''}
            onChange={(e) => setSelectedUser(prev => ({ ...prev, email: e.target.value }))}
            disabled={modalLoading}
          />
          <Input
            label="Duration"
            value={selectedUser?.duration || ''}
            onChange={(e) => setSelectedUser(prev => ({ ...prev, duration: e.target.value }))}
            disabled={modalLoading}
          />
          <Input
            label="Date"
            type="date"
            value={selectedUser?.date || ''}
            onChange={(e) => setSelectedUser(prev => ({ ...prev, date: e.target.value }))}
            disabled={modalLoading}
          />
          <Input
            label="Sending Date"
            type="date"
            value={selectedUser?.sendingDate || ''}
            onChange={(e) => setSelectedUser(prev => ({ ...prev, sendingDate: e.target.value }))}
            disabled={modalLoading}
          />
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
              Domain
            </label>
            <Select
              value={domainOptions.find(option => option.value === selectedUser?.domain) || null}
              onChange={(option) => setSelectedUser(prev => ({ ...prev, domain: option?.value || '' }))}
              options={domainOptions}
              isClearable
              isDisabled={modalLoading}
              className="basic-select"
              classNamePrefix="select"
            />
          </div>
          <Input
            label="Certificate No"
            value={selectedUser?.cerNo || ''}
            onChange={(e) => setSelectedUser(prev => ({ ...prev, cerNo: e.target.value }))}
            disabled={modalLoading}
          />
        </Modal>
      )}
    </Container>
  );
};

export default CertificateUsers;


